import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Field, Input, Section, Spinner, Textarea } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  CspReports,
  Integration,
  MailDnsStatus,
  NotificationSettings,
  SystemStatus,
} from '../lib/types';

/**
 * Sistem Durumu → Kritik durum uyarıları ve e-posta alan adı denetimi (F3B-18).
 */
export function AlertsSection({ alerts }: { alerts: SystemStatus['alerts'] }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<Integration[]>('/v1/admin/integrations'),
    enabled: can('org.integration.read'),
  });
  const tracking = integrations.data?.find((i) => i.key === 'error_tracking');
  const otel = integrations.data?.find((i) => i.key === 'observability');
  const firing = alerts.filter((a) => a.status === 'firing');
  const resolved = alerts.filter((a) => a.status === 'resolved');
  const text = (a: SystemStatus['alerts'][number]) => t(`admin.system.alertKinds.${a.kind}`, a.detail);
  return (
    <Section title={t('admin.system.alertsTitle')} description={t('admin.system.alertsLead')}>
      <div className="space-y-4">
        {firing.length === 0 ? (
          <p className="text-sm text-ink-2">{t('admin.system.alertsNone')}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface" role="list">
            {firing.map((a) => (
              <li key={a.key} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <Badge tone={a.severity === 'critical' ? 'rose' : 'amber'}>
                  {t(`admin.system.severity_${a.severity}`)}
                </Badge>
                <span className="min-w-0 flex-1 font-medium">{text(a)}</span>
                <span className="text-sm text-ink-3">
                  {t('admin.system.alertSince', { time: formatDate(a.firstSeenAt, true) })} ·{' '}
                  {a.lastNotifiedAt
                    ? t('admin.system.alertLastMail', { time: formatDate(a.lastNotifiedAt, true) })
                    : t('admin.system.alertNotMailed')}
                </span>
              </li>
            ))}
          </ul>
        )}
        {resolved.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-ink-2">
              {t('admin.system.alertsResolved', { count: resolved.length })}
            </summary>
            <ul className="mt-2 space-y-1 ps-4">
              {resolved.map((a) => (
                <li key={a.key}>
                  {text(a)} — {t('admin.system.alertResolvedAt', { time: formatDate(a.resolvedAt!, true) })}
                </li>
              ))}
            </ul>
          </details>
        )}
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span>{t('admin.system.errorTracking')}:</span>
          <Badge tone={tracking?.status === 'LIVE' ? 'moss' : 'amber'}>
            {tracking?.status === 'LIVE'
              ? t('admin.system.errorTrackingLive')
              : t('admin.system.errorTrackingSetup')}
          </Badge>
          {can('org.integration.read') && (
            <Link to="/entegrasyonlar" className="text-pine hover:underline">
              {t('admin.nav.integrations')}
            </Link>
          )}
          <HelpTip k="system.error_tracking" />
        </p>
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span>{t('admin.system.observability')}:</span>
          <Badge tone={otel?.status === 'LIVE' ? 'moss' : 'amber'}>
            {otel?.status === 'LIVE'
              ? t('admin.system.errorTrackingLive')
              : t('admin.system.errorTrackingSetup')}
          </Badge>
          <HelpTip k="system.observability" />
        </p>
        <RecipientsEditor />
      </div>
    </Section>
  );
}

function RecipientsEditor() {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<NotificationSettings>('/v1/admin/settings/notifications'),
    enabled: can('org.settings.read'),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? (q.data?.adminEmails ?? []).join('\n');
  const emails = text
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const save = useApiMutation(
    () => api.put('/v1/admin/settings/notifications', { ...q.data, adminEmails: emails }),
    {
      invalidate: [['settings', 'notifications']],
      success: t('admin.system.recipientsSaved'),
      onSuccess: () => setDraft(null),
    },
  );
  if (!can('org.settings.read')) return null;
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return (
    <form
      className="max-w-xl space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(undefined);
      }}
    >
      <Field
        label={t('admin.system.recipients')}
        description={t('admin.system.recipientsHint')}
        help={<HelpTip k="system.alert_recipients" />}
        error={fieldError(save.error, 'adminEmails') ?? fieldError(save.error, 'adminEmails.0')}
      >
        {(p) => (
          <Textarea
            {...p}
            className="min-h-20"
            value={text}
            disabled={!can('org.settings.update')}
            onChange={(e) => setDraft(e.target.value)}
          />
        )}
      </Field>
      {can('org.settings.update') && (
        <Button type="submit" loading={save.isPending} disabled={draft === null}>
          {t('admin.system.recipientsSave')}
        </Button>
      )}
    </form>
  );
}

const DNS_TONE: Record<string, 'moss' | 'amber' | 'rose' | 'slate'> = {
  ok: 'moss',
  monitoring: 'amber',
  missing: 'rose',
  multiple: 'rose',
  not_found: 'amber',
  unknown: 'slate',
};

export function MailDnsSection() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const settings = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<NotificationSettings>('/v1/admin/settings/notifications'),
    enabled: can('org.settings.read'),
  });
  const saved = settings.data?.dkimSelector ?? '';
  const [selector, setSelector] = useState<string | null>(null);
  const sel = selector ?? saved;
  const q = useQuery({
    queryKey: ['mail-dns', saved],
    queryFn: () =>
      api.get<MailDnsStatus>(
        `/v1/admin/system/mail-dns${saved ? `?selector=${encodeURIComponent(saved)}` : ''}`,
      ),
    enabled: !can('org.settings.read') || settings.isSuccess,
  });
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/settings/notifications', {
        ...settings.data,
        dkimSelector: sel.trim() || undefined,
      }),
    {
      invalidate: [['settings', 'notifications']],
      success: t('common.saved'),
      onSuccess: () => setSelector(null),
    },
  );
  const d = q.data;
  const rows = d?.result
    ? ([
        ['spf', d.result.spf.status, d.result.spf.record],
        [
          'dkim',
          d.result.dkim.status,
          d.result.dkim.selector ? `${d.result.dkim.selector}._domainkey` : null,
        ],
        ['dmarc', d.result.dmarc.status, d.result.dmarc.record],
      ] as const)
    : [];
  return (
    <Section title={t('admin.system.mailDnsTitle')} description={t('admin.system.mailDnsLead')}>
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !d?.domain ? (
        <p className="text-sm text-ink-2">{t('admin.system.mailDnsNone')}</p>
      ) : (
        <div className="space-y-4">
          <p className="flex items-center gap-1 text-sm text-ink-2">
            {t('admin.system.mailDnsDomain', {
              domain: d.domain,
              source: t(`admin.system.mailDnsSource_${d.source}`),
              time: d.checkedAt ? formatDate(d.checkedAt, true) : '—',
            })}
            <HelpTip k="system.mail_dns" />
          </p>
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {rows.map(([name, status, record]) => (
              <li key={name} className="space-y-1 px-4 py-3">
                <p className="flex flex-wrap items-center gap-2">
                  <span className="w-16 font-medium">{t(`admin.system.dns.${name}`)}</span>
                  <Badge tone={DNS_TONE[status] ?? 'slate'}>{t(`admin.system.dns.${name}_${status}`)}</Badge>
                </p>
                {record && <p className="break-all font-mono text-xs text-ink-3">{record}</p>}
              </li>
            ))}
          </ul>
          {can('org.settings.update') && (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(undefined);
              }}
            >
              <Field
                label={t('admin.system.dkimSelector')}
                description={t('admin.system.dkimSelectorHint')}
                help={<HelpTip k="system.dkim_selector" />}
                error={fieldError(save.error, 'dkimSelector')}
                className="max-w-xs"
              >
                {(p) => <Input {...p} value={sel} onChange={(e) => setSelector(e.target.value)} />}
              </Field>
              <Button type="submit" loading={save.isPending} disabled={selector === null}>
                {t('admin.system.dkimSelectorSave')}
              </Button>
            </form>
          )}
        </div>
      )}
    </Section>
  );
}

/** Engellenen kaynak anahtar sözcükleri (sunucu bunları köken yerine yazar). */
const CSP_KEYWORDS = new Set(['inline', 'eval', 'wasm-eval', 'data', 'blob', 'self']);

/**
 * Sistem Durumu → Vitrin içerik güvenliği ihlalleri (F11-11): tarayıcının bildirdiği CSP engellemeleri (özet; son
 * görülene göre). Yeni bir etiket, video veya ödeme çerçevesi engelleniyorsa ya da sayfaya dışarıdan kod eklenmeye
 * çalışılıyorsa burada görünür.
 */
export function CspReportsSection() {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const q = useQuery({
    queryKey: ['system', 'csp-reports'],
    queryFn: () => api.get<CspReports>('/v1/admin/system/csp-reports'),
  });
  const d = q.data;
  const manyStores = new Set(d?.items.map((r) => r.storeId)).size > 1;
  const blocked = (v: string) => (CSP_KEYWORDS.has(v) ? t(`admin.system.cspKeyword_${v}`) : v);
  return (
    <Section
      title={t('admin.system.cspTitle')}
      description={t('admin.system.cspLead', { days: d?.retentionDays ?? 30 })}
      action={<HelpTip k="system.csp_reports" />}
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !d || d.items.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.system.cspNone', { days: d?.retentionDays ?? 30 })}</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-2">
            {t('admin.system.cspSummary', {
              rows: formatNumber(d.totals.rows),
              hits: formatNumber(d.totals.hits),
              lastDay: formatNumber(d.totals.lastDay),
            })}
          </p>
          <div className="overflow-x-auto rounded-md border border-line bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-4 py-2 text-start font-medium">{t('admin.system.cspDirective')}</th>
                  <th className="px-4 py-2 text-start font-medium">{t('admin.system.cspBlocked')}</th>
                  <th className="px-4 py-2 text-start font-medium">{t('admin.system.cspPage')}</th>
                  <th className="px-4 py-2 text-end font-medium">{t('admin.system.cspHits')}</th>
                  <th className="px-4 py-2 text-start font-medium">{t('admin.system.cspLastSeen')}</th>
                </tr>
              </thead>
              <tbody>
                {d.items.map((r) => (
                  <tr key={r.id} className="border-t border-line align-top">
                    <td className="px-4 py-2">
                      <span className="font-mono text-xs">{r.directive}</span>
                      {r.disposition === 'report' && (
                        <Badge tone="slate" className="ms-2">
                          {t('admin.system.cspReportOnly')}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 break-all">
                      {blocked(r.blocked)}
                      {r.sourceFile && (
                        <p className="mt-1 font-mono text-xs text-ink-3">
                          {t('admin.system.cspSource', {
                            file: r.sourceFile,
                            line: r.lineNumber ?? '—',
                          })}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2 break-all">
                      <span className="font-mono text-xs">{r.documentPath}</span>
                      {manyStores && <p className="text-xs text-ink-3">{r.storeName}</p>}
                    </td>
                    <td className="tabular px-4 py-2 text-end">{formatNumber(r.hits)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-ink-2">
                      {formatDate(r.lastSeenAt, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Section>
  );
}
